import { useState, useEffect, useCallback, useRef } from 'react';
import { usePinchZoom } from '@/hooks/usePinchZoom';
import { rpc, RpcError } from '@/api/client';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { LapInfo } from './LapInfo';
import { createSpeedChart, createAccelerationChart, createRpmChart, createGForceChart, createPinnedPointPlugin, type ZoomPluginOpts, type TrackMapApi, type PinnedPointHandle } from '@/lib/charts';
import type { TrackColorMode } from '@/lib/track';
import { formatDynamicCornerLabel } from '@/lib/spline';
import { LapTrackMap } from './LapTrackMap';
import { useI18n } from '@/context/I18nContext';
import { useRouter } from '@/context/RouterContext';
import type { LapTelemetryResponse, AnalyzeSectorResponse, SessionDetailResponse, TelemetrySample } from '@/types/telemetry';
import { RotateCcw, ArrowLeft, Gauge, Activity, Zap, TrendingUp } from 'lucide-react';

type ChartTab = 'speed' | 'acceleration' | 'rpm' | 'gforce';

const TABS: { key: ChartTab; labelKey: 'lapAnalysis.speed' | 'lapAnalysis.acceleration' | 'lapAnalysis.rpm' | 'lapAnalysis.gForce'; unit: string; icon: typeof Gauge }[] = [
  { key: 'speed', labelKey: 'lapAnalysis.speed', unit: 'km/h', icon: Gauge },
  { key: 'acceleration', labelKey: 'lapAnalysis.acceleration', unit: 'm/s²', icon: TrendingUp },
  { key: 'rpm', labelKey: 'lapAnalysis.rpm', unit: '', icon: Activity },
  { key: 'gforce', labelKey: 'lapAnalysis.gForce', unit: 'G', icon: Zap },
];

interface LapAnalysisProps {
  sessionId: string;
  lapNumber: number;
  tab?: string;
}

export function LapAnalysis({ sessionId, lapNumber, tab }: LapAnalysisProps) {
  const { t } = useI18n();
  const { navigate } = useRouter();
  const [data, setData] = useState<LapTelemetryResponse | null>(null);
  const [refSamples, setRefSamples] = useState<TelemetrySample[]>([]);
  const [trackShape, setTrackShape] = useState<any | null>(null);
  const [trackAnnotations, setTrackAnnotations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);

  const activeTab: ChartTab = (tab === 'acceleration' || tab === 'rpm' || tab === 'gforce') ? tab : 'speed';

  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<any>(null);
  const trackMapApiRef = useRef<TrackMapApi | null>(null);
  const pinnedHandleRef = useRef<PinnedPointHandle | null>(null);
  const pinnedIdxRef = useRef<number>(-1);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const telemetryData = await rpc<LapTelemetryResponse>('GetLapTelemetry', { sessionId, lapNumber });
      // Batch setData + setLoading(false) so the chart container is in the
      // DOM when the chart-rendering effect fires in response to `data` changing.
      // Previously setLoading(false) was in the finally block, causing an
      // intermediate render where data was set but loading was still true —
      // the chart effect would fire, find no container, and never retry.
      setData(telemetryData);
      setLoading(false);

      // Fetch reference track and track shape (non-critical, loaded after main data)
      try {
        const sessionData = await rpc<SessionDetailResponse>('GetSession', { sessionId });
        const trackId = sessionData.session.trackId;

        try {
          const shapeResp = await rpc<any>('GetTrackShapeAndAnnotations', { trackId });
          if (shapeResp) {
            setTrackShape(shapeResp.shape || null);
            setTrackAnnotations(shapeResp.annotations || []);
          }
        } catch (shapeErr) {
          console.warn('Failed to fetch track shape, using fallback:', shapeErr);
        }

        const refResp = await rpc<AnalyzeSectorResponse>('AnalyzeSector', { trackId, sessionIds: [sessionId] });
        if (refResp.referenceTrack) {
          setRefSamples(refResp.referenceTrack);
        }
      } catch (err) {
        console.warn('Failed to fetch reference track', err);
      }
    } catch (e: any) {
      const nf = e instanceof RpcError && e.isNotFound;
      setNotFound(nf);
      setError(nf ? 'The requested resource could not be found.' : (e.message || t('lapAnalysis.failedToLoad')));
      setLoading(false);
    }
  }, [sessionId, lapNumber, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const onQueueCompleted = () => {
      if (!data?.amendedAnalysis) {
        loadData();
      }
    };
    window.addEventListener('queue:completed', onQueueCompleted);
    return () => window.removeEventListener('queue:completed', onQueueCompleted);
  }, [data?.amendedAnalysis, loadData]);

  // Render the active chart when tab changes or data loads
  useEffect(() => {
    if (!data || !data.samples || data.samples.length === 0) return;
    if (!chartRef.current) return;

    let alive = true;

    // Destroy previous chart instance
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    const timer = requestAnimationFrame(() => {
      if (!alive || !chartRef.current) return;
      chartRef.current.innerHTML = '';

      const samples = data.samples;
      const trackMapApi: TrackMapApi = trackMapApiRef.current || { setHighlight() {}, setRange() {}, setPinnedPoint() {} } as any;
      const fullMin = 0;
      const fullMax = (samples.length - 1) * 0.1;

      // Create pinned point plugin with drag-to-scrub support
      const pinnedHandle = createPinnedPointPlugin({
        onIndexChange: (sampleIdx: number) => {
          pinnedIdxRef.current = sampleIdx;
          trackMapApiRef.current?.setPinnedPoint(sampleIdx);
        },
      });
      pinnedHandleRef.current = pinnedHandle;

      // Zoom opts
      const charts: any[] = [];
      const zoomOpts: ZoomPluginOpts = {
        trackMapApi,
        chartsRef: charts,
        fullMin,
        fullMax,
        onZoomChange: (zoomed) => {
          if (alive) setIsZoomed(zoomed);
        },
      };

      let chart: any = null;
      switch (activeTab) {
        case 'acceleration':
          chart = createAccelerationChart(chartRef.current!, samples, trackMapApi, zoomOpts, pinnedHandle.plugin);
          break;
        case 'rpm':
          chart = createRpmChart(chartRef.current!, samples, trackMapApi, zoomOpts, pinnedHandle.plugin);
          break;
        case 'gforce':
          chart = createGForceChart(chartRef.current!, samples, trackMapApi, zoomOpts, pinnedHandle.plugin);
          break;
        default:
          chart = createSpeedChart(chartRef.current!, samples, trackMapApi, zoomOpts, pinnedHandle.plugin);
      }

      chartInstanceRef.current = chart;
      if (chart) charts.push(chart);

      // Restore pinned point if one was selected before tab switch.
      // IMPORTANT: Must be deferred to the next frame — calling redraw()
      // in the same rAF as chart construction interrupts uPlot's deferred
      // initialization and corrupts rendering.
      if (pinnedIdxRef.current >= 0 && chart) {
        requestAnimationFrame(() => {
          if (!alive) return;
          pinnedHandle.setPinned(pinnedIdxRef.current, chart);
        });
      }
    });

    return () => {
      alive = false;
      cancelAnimationFrame(timer);
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [data, activeTab]);

  const handleResetZoom = useCallback(() => {
    if (!data) return;
    const fullMin = 0;
    const fullMax = (data.samples.length - 1) * 0.1;
    const chart = chartInstanceRef.current;
    if (chart) chart.setScale('x', { min: fullMin, max: fullMax });
    trackMapApiRef.current?.setRange(-1, -1);
    setIsZoomed(false);
  }, [data]);

  const handleTabChange = useCallback((newTab: ChartTab) => {
    navigate(`#/session/${sessionId}/lap/${lapNumber}/${newTab}`, true);
  }, [navigate, sessionId, lapNumber]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} isNotFound={notFound} />;
  if (!data || !data.samples || data.samples.length === 0) {
    return <ErrorState message={t('lapAnalysis.noTelemetry')} />;
  }

  return (
    <div>
      {/* Back */}
      <button
        onClick={() => window.history.back()}
        className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors mb-4 cursor-pointer"
      >
        <ArrowLeft size={14} />
        {t('common.back')}
      </button>

      <h1 className="text-xl font-semibold tracking-tight mb-6">
        {t('lapAnalysis.title', { n: lapNumber })}
      </h1>

      {/* Floating reset zoom */}
      {isZoomed && (
        <div className="sticky top-[64px] sm:top-[56px] z-40 flex justify-end mb-2">
          <button
            onClick={handleResetZoom}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-text-primary bg-bg-raised border border-border-default rounded-md px-3 py-1.5 hover:border-border-hover transition-colors shadow-lg"
          >
            <RotateCcw size={12} /> {t('lapAnalysis.resetZoom')}
          </button>
        </div>
      )}

      <div className="flex flex-col gap-6">
        <div className="w-full max-w-xl mx-auto static md:sticky md:top-[52px] lg:static lg:z-auto">
          <LapTrackMap
            samples={data.samples}
            refSamples={refSamples}
            trackShape={trackShape}
            trackAnnotations={trackAnnotations}
            upshiftIndices={data.upshiftIndices ?? []}
            brakingPointIndices={data.brakingPointIndices ?? []}
            accelerationPointIndices={data.accelerationPointIndices ?? []}
            activeMode={activeTab as TrackColorMode}
            onPointClick={(sampleIdx: number) => {
              pinnedIdxRef.current = sampleIdx;
              if (pinnedHandleRef.current && chartInstanceRef.current) {
                pinnedHandleRef.current.setPinned(sampleIdx, chartInstanceRef.current);
              }
            }}
            apiRef={trackMapApiRef}
          />
        </div>

        <LapInfo lap={data.lap} amendedAnalysis={data.amendedAnalysis} />

        {/* Tab bar */}
        <div className="flex gap-1 p-1 rounded-lg bg-white/[0.03] border border-border-default overflow-x-auto no-scrollbar">
          {TABS.map(tabDef => {
            const Icon = tabDef.icon;
            return (
              <button
                key={tabDef.key}
                onClick={() => handleTabChange(tabDef.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === tabDef.key
                    ? 'bg-accent/10 text-accent'
                    : 'text-text-muted hover:text-text-secondary hover:bg-white/[0.05]'
                }`}
              >
                <Icon size={14} />
                {t(tabDef.labelKey)}
              </button>
            );
          })}
        </div>

        {/* Active chart */}
        <div className="min-w-0 w-full">
          <ChartCard title={(() => { const tab = TABS.find(tb => tb.key === activeTab)!; return tab.unit ? `${t(tab.labelKey)} (${tab.unit})` : t(tab.labelKey); })()}>
            <div ref={chartRef} className="chart-container w-full min-h-[220px] overflow-hidden" />
          </ChartCard>
        </div>

        {/* Amended Corner & Braking Breakdown */}
        {data.amendedAnalysis?.corners && data.amendedAnalysis.corners.length > 0 && (
          <ChartCard title={t('lapAnalysis.amendedCornersTitle')}>
            <div className="overflow-x-auto -mx-4 px-4">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b border-border-subtle text-text-muted text-[0.68rem] uppercase tracking-wider">
                    <th className="py-2 pr-3 text-left font-semibold whitespace-nowrap">{t('lapAnalysis.cornerCol')}</th>
                    <th className="py-2 px-3 min-w-[135px] text-right font-semibold whitespace-nowrap">{t('lapAnalysis.topApproachSpeed')}</th>
                    <th className="py-2 px-3 min-w-[120px] text-right font-semibold whitespace-nowrap">{t('lapAnalysis.slowestSpeed')}</th>
                    <th className="py-2 px-3 min-w-[120px] text-right font-semibold whitespace-nowrap">{t('lapAnalysis.apexSpeed')}</th>
                    <th className="py-2 px-3 min-w-[120px] text-right font-semibold whitespace-nowrap">{t('lapAnalysis.exitSpeed')}</th>
                    <th className="py-2 px-3 text-right font-semibold whitespace-nowrap">{t('lapAnalysis.brakingDist')}</th>
                    <th className="py-2 pl-3 min-w-[115px] text-right font-semibold whitespace-nowrap">{t('lapAnalysis.cornerTime')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.amendedAnalysis.corners.map((corner, idx) => (
                    <tr
                      key={corner.cornerId || idx}
                      onClick={() => {
                        const sampleIdx = corner.slowestSpeedSampleIdx ?? corner.brakingPointSampleIdx ?? 0;
                        pinnedIdxRef.current = sampleIdx;
                        trackMapApiRef.current?.setPinnedPoint(sampleIdx);
                        if (pinnedHandleRef.current && chartInstanceRef.current) {
                          pinnedHandleRef.current.setPinned(sampleIdx, chartInstanceRef.current);
                        }
                      }}
                      className="border-b border-border-subtle/50 last:border-b-0 hover:bg-bg-hover/60 transition-colors cursor-pointer"
                    >
                      <td className="py-2.5 pr-3 font-medium text-text-primary whitespace-nowrap">
                        {formatDynamicCornerLabel(corner.name, idx + 1, 'T')}
                      </td>
                      <td className="py-2.5 px-3 min-w-[135px] text-right mono tabular-nums text-text-secondary whitespace-nowrap">
                        {corner.highestSpeedBeforeBrakingKmh.toFixed(1)} km/h
                      </td>
                      <td className="py-2.5 px-3 min-w-[120px] text-right mono tabular-nums text-text-primary font-semibold whitespace-nowrap">
                        {corner.slowestSpeedKmh.toFixed(1)} km/h
                      </td>
                      <td className="py-2.5 px-3 min-w-[120px] text-right mono tabular-nums text-text-secondary whitespace-nowrap">
                        {corner.apexSpeedKmh.toFixed(1)} km/h
                      </td>
                      <td className="py-2.5 px-3 min-w-[120px] text-right mono tabular-nums text-text-secondary whitespace-nowrap">
                        {corner.exitSpeedKmh.toFixed(1)} km/h
                      </td>
                      <td className="py-2.5 px-3 text-right mono tabular-nums text-text-secondary whitespace-nowrap">
                        {corner.brakingDistanceM.toFixed(1)} m
                      </td>
                      <td className="py-2.5 pl-3 min-w-[115px] text-right mono tabular-nums text-text-secondary whitespace-nowrap">
                        {(corner.transitDurationMs / 1000).toFixed(3)} s
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartCard>
        )}
      </div>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-4">
      <div className="mb-3">
        <span className="section-title">{title}</span>
      </div>
      {children}
    </div>
  );
}
